import { PATHS } from '@/app/router/routes'
import { formatCode } from '@/shared/constants/config'
import {
  CREDIT_STATUS_COLORS,
  CREDIT_STATUS_LABELS,
  type CreditStatusValue,
  formatInstantDate,
  formatLocalDate,
  HANDOVER_STATUS_COLORS,
  HANDOVER_STATUS_LABELS,
  type HandoverRow,
  type HandoverStatusValue
} from '../models/delivery-v2'
import { deliveryDestination } from './delivery-destination'

/**
 * El resumen del panel lateral de Postventa (T10.6b): negociación · crédito · cliente.
 *
 * Es una función pura sobre la FILA del listado, sin pedir nada más al servidor: todo lo que el
 * panel muestra ya viaja en la fila (el expediente de Créditos llega como `credit`). Así el panel
 * abre instantáneo y se puede probar sin montar React.
 */

export type DeliveryPanelClient = {
  id: string
  name: string
  photo: string | null
  caption: string
}

export type DeliveryPanelCredit = {
  id: string
  code: number
  statusLabel: string
  statusClassName: string
  /** «Dónde va»: la etapa en curso, o que el trámite ya se aprobó. */
  position: string
  approvedAt: string
  path: string
}

export type DeliveryPanelAction = {
  label: string
  path: string
}

export type DeliveryPanelSummary = {
  title: string
  handover: {
    statusLabel: string
    statusClassName: string
    act: string
    scheduledDate: string
    completedAt: string
  }
  negotiation: {
    code: string
    property: string
    propertyCode: string | null
    projectName: string | null
    projectCode: string | null
    advisorName: string | null
    advisorAvatar: string | null
    closedAt: string
    path: string
  }
  /** `null` = la fila no tiene expediente vivo (actas heredadas, de antes de Créditos). */
  credit: DeliveryPanelCredit | null
  /** Propietarios con el PRINCIPAL primero. */
  clients: DeliveryPanelClient[]
  /** «Abrir acta» si existe, «Iniciar acta» si no: la acción principal del panel. */
  primaryAction: DeliveryPanelAction
}

function creditPosition(credit: NonNullable<HandoverRow['credit']>): string {
  if (credit.status === 'approved') return 'Trámite aprobado'
  if (credit.status === 'cancelled') return 'Trámite cancelado'
  return credit.currentStageName ?? 'Etapa sin resolver'
}

/**
 * Los propietarios, con el principal arriba.
 *
 * El servidor ya los manda así, pero el orden es una regla del dominio que el panel afirma por su
 * cuenta: el primero es a quien se le entrega, y un cambio en el pipeline no debería poder moverlo.
 * `sort` es estable, así que entre los no principales se respeta el orden de llegada.
 */
function orderedClients(row: HandoverRow): DeliveryPanelClient[] {
  return [...row.clients]
    .sort((left, right) => Number(right.isDefault) - Number(left.isDefault))
    .map((client) => ({
      id: client.id,
      name: client.name,
      photo: client.photo,
      caption: [
        client.code !== null ? `Cliente ${client.code}` : null,
        client.isDefault ? 'Propietario principal' : null
      ]
        .filter(Boolean)
        .join(' · ')
    }))
}

export function describeDeliveryPanel(row: HandoverRow): DeliveryPanelSummary {
  const status = row.status as HandoverStatusValue
  const credit = row.credit
    ? {
        id: row.credit.id,
        code: row.credit.code,
        statusLabel: CREDIT_STATUS_LABELS[row.credit.status as CreditStatusValue],
        statusClassName: CREDIT_STATUS_COLORS[row.credit.status as CreditStatusValue],
        position: creditPosition(row.credit),
        approvedAt: formatInstantDate(row.credit.approvedAt),
        path: PATHS.admin.credits.detail(row.credit.id)
      }
    : null

  return {
    title: formatCode(row.code, 'negotiation'),
    handover: {
      statusLabel: HANDOVER_STATUS_LABELS[status] ?? status,
      statusClassName: HANDOVER_STATUS_COLORS[status] ?? '',
      act: row.delivery ? `Acta #${row.delivery.code}` : 'Sin acta',
      scheduledDate: formatLocalDate(row.delivery?.scheduledDate),
      completedAt: formatInstantDate(row.delivery?.completedAt)
    },
    negotiation: {
      code: formatCode(row.code, 'negotiation'),
      property: row.property?.name ?? '—',
      propertyCode: row.property?.code ?? null,
      projectName: row.project?.name ?? null,
      projectCode: row.project?.code ?? null,
      advisorName: row.advisor?.fullname ?? null,
      advisorAvatar: row.advisor?.avatar ?? null,
      closedAt: formatInstantDate(row.closedAt),
      path: PATHS.admin.negotiations.view(row.id)
    },
    credit,
    clients: orderedClients(row),
    primaryAction: {
      label: row.delivery ? 'Abrir acta' : 'Iniciar acta',
      path: deliveryDestination(row)
    }
  }
}

/**
 * Qué panel abrir para el `?negociacion=` de la URL.
 *
 * ☠️ ES EL DEEP-LINK DE «ABRIR POSTVENTA» DESDE CRÉDITOS. Antes ese enlace saltaba directo al acta;
 * ahora aterriza en el panel de la fila, y el acta queda a un clic en su acción principal (E4).
 *
 * `canResolve` separa «todavía no llegaron filas» de «llegaron y la negociación no está»: sólo en el
 * segundo caso se puede limpiar el parámetro. Limpiarlo antes descartaría un enlace legítimo por
 * llegar primero que su listado.
 */
export function resolveDeliveryPanel(
  rowId: string | null,
  rows: HandoverRow[]
): { row: HandoverRow | null; canResolve: boolean } {
  if (!rowId) return { row: null, canResolve: false }
  return {
    row: rows.find((candidate) => candidate.id === rowId) ?? null,
    canResolve: rows.length > 0
  }
}
